import DropdownMenu, { CLASSES } from 'components/DropdownMenu';
import { CLASSES as TOOLTIP_CLASSES } from 'components/Tooltip';
import { fromJS } from 'immutable';
import { FormattedMessage, injectIntl } from 'react-intl';

// STYLES
import { PaginationContainer, PreviousButton, NextButton, PageNumber } from './Pagination.styles';

import { MAX_SIDE_PAGINATION_PAGES, PER_PAGE } from 'constants/table';

/**
 * Handles the pagination of the table
 */
const Pagination = (props) => {
  // Gets the previous page if it exists
  const handleGetPreviousPage = () => {
    if (props.current === 1) {
      return;
    }

    props.onGetPreviousPage();
  };

  // Gets the next page if it exists
  const handleGetNextPage = () => {
    if (props.current === props.total) {
      return;
    }

    props.onGetNextPage();
  };

  // Gets the requested page number
  const handleGetPage = (page) => () => {
    props.onGetPage(page);
  };

  // Changes the per page value
  const handleChangePerPageValue = (perPage) => () => {
    props.onChangePerPage(perPage);
  };

  // Helper that creates an array of page numbers in sequence
  const getPageIterations = (start) => {
    const pageNumbers = [];

    // Add the first pages starting from the current one
    for (let index = 0; index < MAX_SIDE_PAGINATION_PAGES; index++) {
      // But we want to stop if there's just too many pages
      if (start + index > props.total) {
        break;
      }

      pageNumbers.push(start + index);
    }

    return pageNumbers;
  };

  // Given an array of page numbers, it creates components to display
  const getPageNumberComponents = (pageNumbers) =>
    pageNumbers.map((pageNumber, key) => (
      <PageNumber
        // eslint-disable-next-line react/no-array-index-key
        key={`${pageNumber}-${key}`}
        active={pageNumber === props.current}
        onClick={handleGetPage(pageNumber)}
      >
        {pageNumber}
      </PageNumber>
    ));

  // Gets the page numbers to display
  const getPageNumbers = () => {
    // If we can display all pages, let's do that
    if (props.total <= MAX_SIDE_PAGINATION_PAGES * 2) {
      return getPageNumberComponents(getPageIterations(1));
    }

    /**
     * If we cannot display all pages and the current page is within
     * the first half or the last half of the maximum pages to display
     * on the sides
     */
    if (props.current <= MAX_SIDE_PAGINATION_PAGES || props.current >= props.total - MAX_SIDE_PAGINATION_PAGES + 1) {
      const firstHalf = getPageNumberComponents(getPageIterations(1));
      const lastHalf = getPageNumberComponents(getPageIterations(props.total - MAX_SIDE_PAGINATION_PAGES + 1));

      return (
        <span>
          {firstHalf}
          <PageNumber>...</PageNumber>
          {lastHalf}
        </span>
      );
    }

    /**
     * If it's in the middle, we'll display one page at the beginning and one at the end
     * while keeping the middle at the same size as the max side pagination defined
     *
     * It's important to keep the current page in the middle for easy navigation
     */
    if (props.current > MAX_SIDE_PAGINATION_PAGES && props.current < props.total - MAX_SIDE_PAGINATION_PAGES + 1) {
      const middle = getPageNumberComponents(getPageIterations(props.current - MAX_SIDE_PAGINATION_PAGES / 2));

      return (
        <span>
          <PageNumber active={props.current === 1} onClick={handleGetPage(1)}>
            1
          </PageNumber>
          <PageNumber>...</PageNumber>
          {middle}
          <PageNumber>...</PageNumber>
          <PageNumber active={props.current === props.total} onClick={handleGetPage(props.total)}>
            {props.total}
          </PageNumber>
        </span>
      );
    }

    // Should never get here but just in case
    return null;
  };

  // Gets the items to pass to the per page dropdown
  const getPerPageItems = () =>
    fromJS(
      PER_PAGE.map((value) => ({
        id: String(value),
        renderedValue: String(value),
        handleClick: handleChangePerPageValue(value),
      }))
    );

  return (
    <PaginationContainer>
      <PreviousButton disabled={props.current === 1} onClick={handleGetPreviousPage}>
        <i className="icon-caret-left-light" />
        <FormattedMessage id="components.Table.previous" />
      </PreviousButton>

      {getPageNumbers()}

      <NextButton disabled={props.current === props.total} onClick={handleGetNextPage}>
        <FormattedMessage id="components.Table.next" />
        <i className="icon-caret-right-light" />
      </NextButton>

      <DropdownMenu
        className={`${CLASSES.HIGHLIGHTED} ${TOOLTIP_CLASSES.ALIGN_TOP} ${CLASSES.PRIMARY}`}
        renderedValue={{
          translationId: 'components.Table.perPage',
          values: {
            perPage: props.perPage,
          },
        }}
        items={getPerPageItems()}
      />
    </PaginationContainer>
  );
};

export default injectIntl(Pagination);
